import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Switch
} from '@mui/material';
import { BsEyeFill } from 'react-icons/bs';
import { useNavigate } from 'react-router';
import MainCard from 'components/MainCard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Lottie from 'react-lottie';
import lottieLoader from '../../assets/Lottie/NoLoaderData.json';
import { getstudents } from 'services/student/getStudents';
import toast from 'react-hot-toast';
import { sendInvite } from 'services/coach/sendInvite';
import { getInvite } from 'services/coach/getInvite';
import { LinkOutlined, EyeOutlined, CopyOutlined, ShareAltOutlined } from '@ant-design/icons';
import { updateStudentStatus } from 'services/student/updateStudentStatus';
import { getRelativeTime } from 'utils/getRelativeTime'; // make sure path is correct

const loaderOptions = {
  loop: true,
  autoplay: true,
  animationData: lottieLoader,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice'
  }
};

export default function StudentTable() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [inviteLink, setInviteLink] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);

  const coachid = Number(localStorage.getItem('userid'));

  const { data: inviteData, refetch: fetchInvite } = useQuery({
    queryFn: () => getInvite({ coach_id: Number(coachid) }),
    queryKey: ['invite'],
    enabled: false
  });

  const invites = inviteData || [];
  console.log('inviteData:', invites);

  const sendInviteMutation = useMutation({
    mutationFn: sendInvite,
    onSuccess: (data) => {
      // console.log('sendInvite success:', data);
      toast.success(data?.message);
      fetchInvite();
    },
    onError: (err) => {
      console.error('sendInvite error:', err);
      toast.error(err.message || 'Failed to send invite');
    }
  });

  const { data, isLoading, error } = useQuery({
    queryFn: () => getstudents({ coachId: Number(coachid) }),
    queryKey: ['students', coachid] // add coachid here
  });

  const updateStudentStatusMutation = useMutation({
    mutationFn: updateStudentStatus,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['students']);
      toast.success(data?.message || 'Student status updated successfully');
    },
    onError: (err) => {
      console.error('updateStudentStatus error:', err);
      toast.error(err?.response?.data?.message || 'Failed to update student status');
    }
  });

  const studentsData = data?.data || [];
  // console.log('studentsData:', studentsData);

  useEffect(() => {
    const savedLink = localStorage.getItem('inviteLink');
    const savedExpiry = localStorage.getItem('inviteExpiresAt');
    if (savedLink && savedExpiry && new Date(savedExpiry) > new Date()) {
      setInviteLink(savedLink);
      setExpiresAt(new Date(savedExpiry));
    } else {
      localStorage.removeItem('inviteLink');
      localStorage.removeItem('inviteExpiresAt');
      fetchInvite();
    }
  }, [fetchInvite]);

  useEffect(() => {
    const interval = setInterval(() => {
      const savedExpiry = localStorage.getItem('inviteExpiresAt');
      if (savedExpiry && new Date(savedExpiry) < new Date()) {
        localStorage.removeItem('inviteLink');
        localStorage.removeItem('inviteExpiresAt');
        setInviteLink('');
        setExpiresAt(null);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (studentsData?.length) {
      const enriched = studentsData.map((student) => ({
        studentId: student.studentId,
        studentName: student.student_name,
        studentEmail: student.student_email || 'N/A',
        studentContact: student.student_phone || 'N/A',
        registrationDate: student.created_at ? new Date(student.created_at).toLocaleDateString() : 'N/A',
        // progressTrend: student.progress_trend || 'N/A',
        dob: student.dob ? new Date(student.dob).toLocaleDateString() : 'N/A',
        lastActive: student.last_active ? getRelativeTime(new Date(student.last_active)) : 'N/A',
        studentStatus: student.status ?? 1 // ✅ Ensure we pick actual DB status
      }));

      setStudents(enriched);
    }
  }, [studentsData]);

  useEffect(() => {
    if (!expiresAt) return;

    const interval = setInterval(() => {
      const now = new Date();
      const diff = new Date(expiresAt) - now;

      if (diff <= 0) {
        setTimeLeft('');
        setInviteLink('');
        setExpiresAt(null);
        localStorage.removeItem('inviteLink');
        localStorage.removeItem('inviteExpiresAt');
        clearInterval(interval);
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        if (hours > 0) {
          setTimeLeft(`${hours} hour(s), ${minutes} minute(s) left`);
        } else if (minutes > 0) {
          setTimeLeft(`${minutes} minute(s) and ${seconds} second(s) left`);
        } else {
          setTimeLeft(`${seconds} second(s) left`);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  const allCount = students.length;
  const activeCount = students.filter((s) => s.studentStatus === 1).length;
  const inactiveCount = students.filter((s) => s.studentStatus === 2).length;

  const filteredByTab = students.filter((student) => {
    if (tabValue === 1) return student.studentStatus === 1; // Active
    if (tabValue === 2) return student.studentStatus === 2; // Inactive
    return true; // All
  });

  const filteredStudents = filteredByTab.filter((student) => student.studentName.toLowerCase().includes(searchTerm.toLowerCase()));

  const isInviteValid = expiresAt && new Date(expiresAt) > new Date();

  const handleGenerateLink = async () => {
    setGenerateLoading(true); // start loader
    sendInviteMutation.mutate(
      { coach_id: coachid },
      {
        onSettled: () => {
          setGenerateLoading(false); // stop loader after success or error
        }
      }
    );
  };

  const handleShowInvite = async () => {
    try {
      setInviteLoading(true); // start loader
      const result = await fetchInvite();
      const invite = result?.data?.response;

      if (invite?.status && invite?.inviteLink && invite?.expiresAt) {
        const expiry = new Date(invite.expiresAt);
        const isStillValid = expiry > new Date();

        if (!isStillValid) {
          toast.error('Invite link has expired. Please generate a new one.');
          return;
        }

        setInviteLink(invite.inviteLink);
        setExpiresAt(expiry);
        setOpenDialog(true);
      } else {
        toast.error('No valid invite link found.');
      }
    } catch (err) {
      toast.error('Failed to fetch invite link.');
      console.error(err);
    } finally {
      setInviteLoading(false); // stop loader
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleToggleStatus = (student) => {
    const newStatus = student.studentStatus === 1 ? 2 : 1;
    updateStudentStatusMutation.mutate({
      key: newStatus,
      studentid: student.studentId
    });
  };

  //   if (isLoading) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
  //       <Lottie options={loaderOptions} height={200} width={200} />
  //     </Box>
  //   );
  // }

  const buttonText = generateLoading
    ? 'Please wait...'
    : inviteLink && new Date(expiresAt) > new Date()
      ? 'Show Invite Link'
      : 'Generate Invite Link';

  return (
    <MainCard>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All" icon={<Chip label={allCount} color="primary" size="small" />} iconPosition="end" />
          <Tab label="Active" icon={<Chip label={activeCount} color="success" size="small" />} iconPosition="end" />
          <Tab label="Inactive" icon={<Chip label={inactiveCount} color="error" size="small" />} iconPosition="end" />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Search Student" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <Button
            variant="contained"
            startIcon={<LinkOutlined />}
            color="primary"
            onClick={async () => {
              try {
                setGenerateLoading(true);

                // Fetch existing invite
                const result = await fetchInvite();
                const existingInvite = result?.data?.response;

                if (existingInvite?.inviteLink && new Date(existingInvite.expiresAt) > new Date()) {
                  // Valid invite exists
                  setInviteLink(existingInvite.inviteLink);
                  setExpiresAt(new Date(existingInvite.expiresAt));
                  setOpenDialog(true);
                } else {
                  // Generate new invite
                  await sendInviteMutation.mutateAsync({ coach_id: coachid });
                  const newResult = await fetchInvite();
                  const newInvite = newResult?.data?.response;

                  setInviteLink(newInvite.inviteLink);
                  setExpiresAt(new Date(newInvite.expiresAt));
                  setOpenDialog(true);
                }
              } catch (err) {
                toast.error('Failed to fetch/generate invite link');
                console.error(err);
              } finally {
                setGenerateLoading(false);
              }
            }}
            disabled={generateLoading}
          >
            {generateLoading ? 'Please wait...' : 'Invite Student'}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 800 }} aria-label="student table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contact</TableCell>
              {/* <TableCell>Progress Trend</TableCell> */}
              <TableCell>Date of Birth</TableCell>
              <TableCell>Last Active</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents.length > 0 ? (
              filteredStudents.map((student, index) => (
                <TableRow key={student.studentId}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{student.studentName}</TableCell>
                  <TableCell>{student.studentEmail}</TableCell>
                  <TableCell>{student.studentContact}</TableCell>
                  <TableCell>{student.dob}</TableCell>
                  {/* <TableCell>{student.progressTrend}</TableCell> */}
                  <TableCell>{student.lastActive}</TableCell>
                  <TableCell>
                    <Chip
                      label={student.studentStatus === 1 ? 'Active' : 'Inactive'}
                      color={student.studentStatus === 1 ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Tooltip title="View">
                        <IconButton color="secondary" onClick={() => navigate('/students-List/student-profile', { state: { student } })}>
                          <BsEyeFill />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={student.studentStatus === 1 ? 'Deactivate' : 'Activate'}>
                        <Switch checked={student.studentStatus === 1} onChange={() => handleToggleStatus(student)} color="primary" />
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No students found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Invite Link</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              value={inviteLink || 'No valid invite link available.'}
              variant="outlined"
              margin="dense"
              InputProps={{ readOnly: true }}
            />
            {expiresAt && (
              <Box sx={{ mt: 1, color: isInviteValid ? 'text.secondary' : 'error.main' }}>
                Link Expires In: {isInviteValid ? timeLeft : 'This link has expired. Generate a new one.'}
              </Box>
            )}
            {!expiresAt && inviteLink && (
              <Box sx={{ mt: 1, color: 'warning.main' }}>Expiration information unavailable. Generate a new link if needed.</Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Button
              variant="outlined"
              disabled={!inviteLink || !isInviteValid}
              startIcon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard
                  .writeText(inviteLink)
                  .then(() => toast.success('Link copied to clipboard!'))
                  .catch(() => toast.error('Failed to copy invite link'));
              }}
            >
              Copy Link
            </Button>
            <Button
              variant="contained"
              disabled={!inviteLink || !isInviteValid}
              startIcon={<ShareAltOutlined />}
              onClick={() => {
                if (navigator.share) {
                  navigator
                    .share({
                      title: 'Join me on Accupose!',
                      text: 'Check out this invite:',
                      url: inviteLink
                    })
                    .catch((err) => console.log('Share error:', err));
                } else {
                  toast.error('Your browser does not support Web Share.');
                }
              }}
            >
              Share
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
}
